import React, { useState, useEffect, useRef } from "react";
import { Star, ChevronLeft, ChevronRight, ExternalLink, MapPin } from "lucide-react";
import { reviews, SUMMARY, MIN_REVIEWS_TO_SHOW } from "../data/reviews";

const AUTO_ROTATE_MS = 7000;

/**
 * Homepage Google reviews carousel.
 * Renders nothing until at least MIN_REVIEWS_TO_SHOW reviews are populated
 * in /app/frontend/src/data/reviews.js — keeps the page clean while empty.
 */
export default function GoogleReviewsCarousel() {
  const hasReviews = reviews.length >= MIN_REVIEWS_TO_SHOW;
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!hasReviews) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % reviews.length);
    }, AUTO_ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [hasReviews]);

  if (!hasReviews) return null;

  const restart = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setIndex((i) => (i + 1) % reviews.length);
      }, AUTO_ROTATE_MS);
    }
  };

  const go = (dir) => {
    setIndex((i) => (i + dir + reviews.length) % reviews.length);
    restart();
  };

  const r = reviews[index];

  return (
    <section className="py-12 md:py-20 relative overflow-hidden" data-testid="reviews-carousel">
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: "var(--te-primary)" }}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Summary banner */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
          <div>
            <div className="te-overline mb-3">/ What our customers say</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              {SUMMARY.rating}★ on Google,
              <br />
              <span style={{ color: "var(--te-primary)" }}>
                from {SUMMARY.total}+ Adelaide families &amp; businesses.
              </span>
            </h2>
          </div>
          <a
            href={SUMMARY.url}
            target="_blank"
            rel="noopener noreferrer"
            className="te-btn te-btn-ghost shrink-0"
            data-testid="reviews-read-all"
          >
            Read all on Google <ExternalLink size={14} />
          </a>
        </div>

        {/* Carousel card */}
        <div
          className="te-card p-8 md:p-12 relative overflow-hidden"
          style={{ background: "var(--te-paper)" }}
          aria-live="polite"
        >
          {/* 5-star bar */}
          <div className="flex gap-1 mb-6" aria-label={`${r.rating} out of 5 stars`}>
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={20}
                fill={i < r.rating ? "var(--te-primary)" : "transparent"}
                stroke="var(--te-primary)"
              />
            ))}
          </div>

          {/* Quote */}
          <blockquote
            key={index}
            className="text-lg sm:text-2xl leading-relaxed tracking-tight text-white font-medium mb-8"
            style={{ animation: "te-fade-in 0.5s ease both" }}
          >
            "{r.text}"
          </blockquote>

          {/* Attribution */}
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <div
              className="w-10 h-10 flex items-center justify-center font-bold text-white"
              style={{ background: "var(--te-primary)" }}
              aria-hidden="true"
            >
              {r.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-zinc-100">{r.name}</div>
              <div className="te-overline text-[10px] flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                {r.suburb && (
                  <span className="flex items-center gap-1">
                    <MapPin size={11} style={{ color: "var(--te-primary)" }} />
                    {r.suburb}
                  </span>
                )}
                {r.tech && <span>TECH: {r.tech}</span>}
                <span>VIA GOOGLE</span>
              </div>
            </div>
          </div>

          {/* Nav arrows */}
          <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-white/10">
            <div className="flex gap-1.5">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setIndex(i); restart(); }}
                  data-testid={`reviews-dot-${i}`}
                  aria-label={`Show review ${i + 1}`}
                  className="h-1.5 transition-all"
                  style={{
                    width: i === index ? 28 : 10,
                    background: i === index ? "var(--te-primary)" : "rgba(255,255,255,0.2)",
                  }}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => go(-1)}
                data-testid="reviews-prev"
                aria-label="Previous review"
                className="w-10 h-10 flex items-center justify-center border border-white/15 hover:border-[var(--te-primary)] hover:text-white text-zinc-400 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => go(1)}
                data-testid="reviews-next"
                aria-label="Next review"
                className="w-10 h-10 flex items-center justify-center border border-white/15 hover:border-[var(--te-primary)] hover:text-white text-zinc-400 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes te-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
